import { useQuery } from '@tanstack/react-query';
import { useRef, useState, type ComponentProps, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssetStatusValue } from '@up2d8/contracts';
import { Button, Card, Dialog, Textarea, cn } from '@up2d8/ui';
import { useSession } from '../../../shared/session';

/**
 * De onderdelen van de ploegoverdracht die op meer dan één scherm staan (Stap 39, OVD-2).
 *
 * De iconen zijn de SVG-paden uit de mockup, letterlijk (spec, deel B). De kleuren zijn de AA-set
 * waarop ONT-0 de `fm-*`-tokens van de spec heeft afgebeeld (`deviations.md`).
 */

/** Spec §2: altijd icoon + woord + kleur, nooit kleur alleen. */
const STATUS_TONE: Record<AssetStatusValue, { tint: string; border: string }> = {
  running: { tint: 'bg-success-tint text-success-tint-text', border: 'border-[var(--success-aa)]' },
  fault: {
    tint: 'bg-brand-orange-tint text-[var(--orange-tint-text-aa)]',
    border: 'border-[var(--brand-orange-aa)]',
  },
  stopped: { tint: 'bg-danger-tint text-danger-tint-text', border: 'border-danger-deep' },
};

export function StatusIcon({ status, size = 14 }: { status: AssetStatusValue; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  if (status === 'running') {
    return (
      <svg {...common} strokeWidth={2.5}>
        <path d="M20 6L9 17l-5-5" />
      </svg>
    );
  }
  if (status === 'fault') {
    return (
      <svg {...common} strokeWidth={2}>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  return (
    <svg {...common} strokeWidth={2}>
      <polygon points="7.86 2 16.14 2 22 7.86 22 16.14 16.14 22 7.86 22 2 16.14 2 7.86 7.86 2" />
      <rect x="9" y="9" width="6" height="6" />
    </svg>
  );
}

/** Het wissel-icoon van de strook en het menu-item. */
export function HandoverIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  );
}

export function StatusBadge({
  status,
  children,
}: {
  status: AssetStatusValue;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <span
      data-status={status}
      className={cn(
        'inline-flex items-center gap-1 rounded-pill px-2.5 py-0.5 font-heading text-small font-semibold',
        STATUS_TONE[status].tint,
      )}
    >
      <StatusIcon status={status} />
      {children ?? t(`handover.status.${status}`)}
    </span>
  );
}

/**
 * Een keuze van twee of drie knoppen naast elkaar, als `radiogroup` (OVD-2, tests).
 *
 * Pijltjestoetsen verplaatsen de keuze, zoals een groep radioknoppen dat doet; alleen de gekozen
 * knop zit in de tabvolgorde. Elk raakvlak is minstens 52 hoog op de telefoon (spec §3, frame 3).
 */
export function Segments<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled = false,
  testId,
}: {
  label: string;
  value: T;
  options: { value: T; label: string; icon?: ReactNode; selectedClass: string }[];
  onChange: (value: T) => void;
  disabled?: boolean;
  testId?: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const delta =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (delta === 0) return;
    event.preventDefault();
    const next = (index + delta + options.length) % options.length;
    const option = options[next];
    if (!option) return;
    onChange(option.value);
    refs.current[next]?.focus();
  };

  return (
    <div role="radiogroup" aria-label={label} className="flex gap-2" data-testid={testId}>
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(element) => {
              refs.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => move(event, index)}
            className={cn(
              'flex min-h-[52px] flex-1 items-center justify-center gap-1.5 rounded-md border-[1.5px] border-border-strong bg-surface px-2 text-content text-text-secondary desk:min-h-8 desk:rounded-pill desk:text-small',
              selected && cn('border-2 font-semibold', option.selectedClass),
              disabled && 'cursor-default opacity-80',
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function statusOptions(t: (key: string) => string) {
  return (['running', 'fault', 'stopped'] as const).map((status) => ({
    value: status,
    label: t(`handover.status.${status}`),
    icon: <StatusIcon status={status} size={18} />,
    selectedClass: cn(STATUS_TONE[status].tint, STATUS_TONE[status].border),
  }));
}

export const OPEN_CLOSE_SELECTED =
  'bg-brand-primary-tint border-[var(--brand-primary-aa)] text-[var(--primary-tint-text-aa)]';

/** Eén foto, met de kortlevende download-URL (ADR-0015). */
export function HandoverPhoto({ fileId, onOpen }: { fileId: string; onOpen?: () => void }) {
  const { api } = useSession();
  const url = useQuery({
    queryKey: ['handover-photo', fileId],
    staleTime: 4 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await api.GET('/api/v1/media/{fileId}/download', {
        params: { path: { fileId }, query: { variant: 'thumbnail' } },
      });
      if (error || !data) return null;
      return (data as { url: string }).url;
    },
  });

  return (
    <button
      type="button"
      onClick={onOpen}
      className="aspect-square overflow-hidden rounded-md bg-border-light"
      data-testid="handover-photo"
    >
      {url.data ? <img src={url.data} alt="" className="h-full w-full object-cover" /> : null}
    </button>
  );
}

/** Een tik vergroot (spec §3, frame 2). */
export function PhotoGrid({ fileIds, extra }: { fileIds: string[]; extra?: ReactNode }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const [open, setOpen] = useState<string | null>(null);

  const big = useQuery({
    queryKey: ['handover-photo-large', open],
    enabled: open !== null,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await api.GET('/api/v1/media/{fileId}/download', {
        params: { path: { fileId: open as string } },
      });
      if (error || !data) return null;
      return (data as { url: string }).url;
    },
  });

  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        {fileIds.map((fileId) => (
          <HandoverPhoto key={fileId} fileId={fileId} onOpen={() => setOpen(fileId)} />
        ))}
        {extra}
      </div>
      <Dialog
        open={open !== null}
        onOpenChange={(value) => !value && setOpen(null)}
        title={t('handover.photo')}
      >
        {big.data ? <img src={big.data} alt="" className="w-full rounded-md" /> : null}
      </Dialog>
    </>
  );
}

/**
 * Het venster "Punt afsluiten" (niet getekend; gebouwd met de bestaande dialoog, `gaps.md`).
 *
 * Een notitie is optioneel: "opgelost" zegt vaak genoeg. Er is geen verwijderknop, ook hier niet.
 */
export function CloseItemDialog({
  text,
  onClose,
  onConfirm,
  busy,
}: {
  text: string;
  onClose: () => void;
  onConfirm: (note: string) => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  return (
    <Dialog open onOpenChange={(value) => !value && onClose()} title={t('handover.closeTitle')}>
      <div className="flex flex-col gap-element">
        <p className="font-heading text-content font-semibold text-text-primary">{text}</p>
        <Textarea
          label={t('handover.closeNote')}
          value={note}
          maxLength={500}
          data-testid="close-note"
          onChange={(event) => setNote(event.target.value)}
        />
        <Button
          size="lg"
          loading={busy}
          onClick={() => onConfirm(note)}
          data-testid="close-confirm"
        >
          {t('handover.closeConfirm')}
        </Button>
      </div>
    </Dialog>
  );
}

/** Spec, vloermodus: een kaart met binnenin `gap: 10px`. */
export function HandoverCard({ className, ...props }: ComponentProps<typeof Card>) {
  return <Card compact className={cn('flex flex-col gap-2.5', className)} {...props} />;
}
